export interface AuthConfig {
  apiKey?: string;
  clientId?: string;
  clientSecret?: string;
  tokenEndpoint?: string;
  scope?: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  scope?: string;
}

export class MIAuth {
  private static apiKey: string | null = null;
  private static token: string | null = null;
  private static tokenExpiry: number = 0;
  private static config: AuthConfig = {};
  private static refreshTimer: ReturnType<typeof setTimeout> | null = null;

  static configure(config: AuthConfig): void {
    MIAuth.config = config;
    if (config.apiKey) {
      MIAuth.apiKey = config.apiKey;
    }
  }

  static getApiKey(): string | null {
    return MIAuth.apiKey;
  }

  static setApiKey(key: string): void {
    MIAuth.apiKey = key;
  }

  static async authenticate(): Promise<string> {
    if (MIAuth.apiKey) return MIAuth.apiKey;

    if (MIAuth.token && Date.now() < MIAuth.tokenExpiry) {
      return MIAuth.token;
    }

    if (!MIAuth.config.clientId || !MIAuth.config.clientSecret) {
      throw new Error('No API key or OAuth2 credentials configured');
    }

    const endpoint = MIAuth.config.tokenEndpoint || 'https://api.migis.mapmy.ai/v2/auth/token';

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'client_credentials',
        client_id: MIAuth.config.clientId,
        client_secret: MIAuth.config.clientSecret,
        scope: MIAuth.config.scope || 'map:read',
      }),
    });

    if (!response.ok) {
      throw new Error(`Authentication failed: ${response.status}`);
    }

    const data: TokenResponse = await response.json();
    MIAuth.token = data.access_token;
    MIAuth.tokenExpiry = Date.now() + data.expires_in * 1000 - 60000;

    MIAuth.scheduleRefresh(data.expires_in);

    return MIAuth.token;
  }

  private static scheduleRefresh(expiresIn: number): void {
    if (MIAuth.refreshTimer) clearTimeout(MIAuth.refreshTimer);
    const refreshIn = Math.max((expiresIn - 120) * 1000, 60000);
    MIAuth.refreshTimer = setTimeout(() => {
      MIAuth.authenticate().catch(console.error);
    }, refreshIn);
  }

  static getAuthHeaders(): Record<string, string> {
    if (MIAuth.token) {
      return { Authorization: `Bearer ${MIAuth.token}` };
    }
    if (MIAuth.apiKey) {
      return { 'X-API-Key': MIAuth.apiKey };
    }
    return {};
  }

  static isAuthenticated(): boolean {
    return !!(MIAuth.apiKey || (MIAuth.token && Date.now() < MIAuth.tokenExpiry));
  }

  static logout(): void {
    MIAuth.token = null;
    MIAuth.tokenExpiry = 0;
    MIAuth.apiKey = null;
    if (MIAuth.refreshTimer) clearTimeout(MIAuth.refreshTimer);
  }

  /**
   * Fetch wrapper that injects auth headers and retries once on 401 by
   * re-authenticating. Use this from any SDK code that talks to
   * api.migis.mapmy.ai (or any endpoint configured to require MIAuth).
   */
  static async fetchWithAuth(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers || {});
    for (const [k, v] of Object.entries(MIAuth.getAuthHeaders())) headers.set(k, v);
    let res = await fetch(input, { ...init, headers });
    if (res.status === 401) {
      // Token may have expired or been revoked. Try a re-auth and retry once.
      try {
        MIAuth.token = null;
        MIAuth.tokenExpiry = 0;
        await MIAuth.authenticate();
        const retryHeaders = new Headers(init.headers || {});
        for (const [k, v] of Object.entries(MIAuth.getAuthHeaders())) retryHeaders.set(k, v);
        res = await fetch(input, { ...init, headers: retryHeaders });
      } catch {
        // Fall through with the original 401
      }
    }
    return res;
  }
}
